import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import { ZoneManager } from './ZoneManager';
import { TableEditor } from './TableEditor';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_PAGE, BG_CARD, FONT_HEADING } from './designTokens';

interface Zone {
  id: string;
  name: string;
  colorCode: string;
  capacity: number | null;
  sortOrder: number;
  setupInstructions?: string | null;
  _count: { tables: number };
}

interface TableRow {
  id: string;
  tableNumber: number;
  tableName: string;
  zoneId: string;
  zone?: { name: string; colorCode: string };
  capacity: number;
  seats?: { status: string }[];
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1.5rem 1rem',
    backgroundColor: BG_PAGE,
  },
  heading: {
    fontFamily: FONT_HEADING,
    fontSize: '1.25rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 1.25rem 0',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1rem',
    gap: '0.75rem',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${BORDER_LIGHT}`,
    borderTopColor: PRIMARY,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '0.9rem',
    color: TEXT_MUTED,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1rem',
    gap: '0.75rem',
  },
  errorIcon: {
    fontSize: '2rem',
  },
  errorText: {
    fontSize: '0.9rem',
    color: '#c62828',
    textAlign: 'center' as const,
  },
  retryButton: {
    marginTop: '0.5rem',
    padding: '0.5rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: PRIMARY,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  section: {
    marginBottom: '1.5rem',
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1.25rem',
  },
  sectionTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 1rem 0',
  },
};

export function SetupView() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [tables, setTables] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [zonesData, tablesData] = await Promise.all([
        apiClient<Zone[]>('/table-management/zones'),
        apiClient<TableRow[]>('/table-management/tables'),
      ]);
      setZones(zonesData);
      setTables(tablesData);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      const message = apiErr?.message || 'Failed to load setup data. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleZoneCreated = useCallback((zone: Zone) => {
    setZones((prev) => [...prev, zone]);
  }, []);

  const handleZoneUpdated = useCallback((zone: Zone) => {
    setZones((prev) => prev.map((z) => (z.id === zone.id ? zone : z)));
  }, []);

  const handleZoneDeleted = useCallback((zoneId: string) => {
    setZones((prev) => prev.filter((z) => z.id !== zoneId));
  }, []);

  const handleTableUpdated = useCallback((table: TableRow) => {
    setTables((prev) => prev.map((t) => (t.id === table.id ? table : t)));
  }, []);

  const handleTableCreated = useCallback((table: TableRow) => {
    setTables((prev) => [...prev, table]);
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <span style={styles.loadingText}>Loading setup data…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={styles.errorIcon}>⚠️</span>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryButton} onClick={fetchData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Setup</h2>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Zones</h3>
        <ZoneManager
          zones={zones}
          tables={tables}
          onZoneCreated={handleZoneCreated}
          onZoneUpdated={handleZoneUpdated}
          onZoneDeleted={handleZoneDeleted}
        />
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Tables</h3>
        <TableEditor
          zones={zones}
          tables={tables}
          onTableUpdated={handleTableUpdated}
          onTableCreated={handleTableCreated}
        />
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Maintenance</h3>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' }}>Recalculate all table status badges based on current seat assignments. Use this if badges show incorrect status (e.g., "Available" when the table is full).</p>
        <button
          style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          onClick={async () => {
            try {
              const result = await apiClient<{ recalculated: number; tables: Array<{ tableNumber: number; status: string }> }>('/table-management/tables/recalculate-all', { method: 'POST' });
              const changed = result.tables.filter((t) => t.status !== 'Available');
              alert(`Recalculated ${result.recalculated} tables.\n\nNon-available: ${changed.map((t) => `T${t.tableNumber}: ${t.status}`).join(', ') || 'none'}`);
            } catch (err: any) {
              alert(err?.message || 'Failed to recalculate.');
            }
          }}
          aria-label="Recalculate all table badges"
        >
          🔄 Recalculate All Badges
        </button>
      </div>
    </div>
  );
}
